import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import ReCAPTCHA from 'react-google-recaptcha';
import { colors } from '../styles/GlobalStyles';
import { submitRegistration, RegistrationFormData } from '../services/api';


// --- Styled Components --- styled-components --- //

const FormWrapper = styled.form`
  max-width: 600px;
  margin: 0 auto;
  padding: 30px 40px;
  background-color: ${colors.white}; /* White background for the form */
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const FormTitle = styled.h2`
  text-align: center;
  margin-bottom: 25px;
  color: ${colors.navyBlue};
`;

const ContractDate = styled.h4`
  text-align: center;
  margin: 25px auto 0px auto;
  color: ${colors.navyBlue};
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: bold; /* Use default Oswald Bold */
  font-size: 0.9em;
  color: ${colors.navyBlue};
  text-transform: uppercase;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${colors.warmGray};
  border-radius: 4px;
  font-size: 1em;
  font-family: sans-serif; /* Use a standard font for input text */

  &:focus {
    outline: none;
    border-color: ${colors.forestGreen};
    box-shadow: 0 0 0 2px rgba(42, 113, 49, 0.2);
  }
`;

const SmallText = styled.small`
  display: block;
  margin-top: 5px;
  font-size: 0.8em;
  color: ${colors.warmGray};
  font-family: sans-serif;
`;

const SubmitButton = styled.button`
  background-color: ${colors.navyBlue};
  color: ${colors.white};
  border: none;
  padding: 12px 25px;
  font-size: 1.1em;
  font-family: 'Oswald', sans-serif; /* Button text Oswald */
  text-transform: uppercase;
  font-weight: 700;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  display: block;
  width: 100%;
  margin-top: 15px;

  &:hover:not(:disabled) {
    background-color: ${colors.forestGreen};
  }

  &:disabled {
    background-color: ${colors.warmGray};
    cursor: not-allowed;
  }
`;

const StatusMessage = styled.p<{ success?: boolean }>`
  text-align: center;
  margin-top: 15px;
  padding: 10px;
  border-radius: 4px;
  font-weight: bold;
  color: ${props => (props.success ? colors.forestGreen : '#D8000C')}; /* Red for error */
  background-color: ${props => (props.success ? '#DFF2BF' : '#FFD2D2')}; /* Light green/red background */
`;

const RecaptchaWrapper = styled.div`
  margin-top: 20px;
  display: flex;
  justify-content: center;
`;

const CheckDiv = styled.div`
  display: inline-block;
  > input {
    opacity: 0;
  }
  > input + label {
    position: relative; /* permet de positionner les pseudo-éléments */
    padding-left: 25px; /* fait un peu d'espace pour notre case à venir */
    cursor: pointer;    /* affiche un curseur adapté */
    &:before {
      content: '';
      position: absolute;
      left:0; top: 1px;
      width: 17px; height: 17px; /* dim. de la case */
      border: 1px solid #aaa;
      background: #f8f8f8;
      border-radius: 3px; /* angles arrondis */
      box-shadow: inset 0 1px 3px rgba(0,0,0,.3) /* légère ombre interne */
    }
    &:after {
      content: '✔';
      position: absolute;
      top: -1px; left: 2px;
      font-size: 16px;
      color: #09ad7e;
      transition: all .2s; /* on prévoit une animation */
    }
  }
  > input:not(:checked) + label {
      &:after {
        opacity: 0; /* coche invisible */
        transform: scale(0); /* mise à l'échelle à 0 */
      }
  }
  > input:disabled:not(:checked) + label {
      &:before {
        box-shadow: none;
        border-color: #bbb;
        background-color: #ddd;
      }
  }
  > input:checked + label {
    &:after {
      opacity: 1; /* coche opaque */
      transform: scale(1); /* mise à l'échelle 1:1 */
    }
  }
  > input:disabled:checked + label {
    &:after {
      color: #999;
    }
  }
  > input:disabled + label {
    color: #aaa;
  }
  > input:checked:focus + label, input:not(:checked):focus + label {
    &:before {
      border: 1px dotted blue;
    }
  }
`;

const TermsWrapper = styled.div`
    max-width: 600px;
    margin: 20px auto;
    padding: 30px 40px;
    background-color: ${colors.white}; /* White background for the form */
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const TermsWrapperOne = styled.div`
    max-width: 550px;
    height: 400px;
    overflow-y: scroll;
    margin: 20px auto;
    //margin-bottom: 20px;
    padding: 30px 40px;
    background-color: ${colors.white}; /* White background for the form */
    border-radius: 8px;
    //box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    border: 1px solid rgba(0, 0, 0, 0.1); 
`;

// --- Component --- //

// TODO: Replace with your actual site key (store in .env)
// const RECAPTCHA_SITE_KEY = '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI'; // Google's Test Key
const RECAPTCHA_SITE_KEY = '6LfjIkArAAAAACIy4Si-RbG2bdPsxohydg8ACvLv';
const RegistrationForm: React.FC = () => {
  const [formData, setFormData] = useState<RegistrationFormData>({
    name: '',
    email: '',
    phoneNumber: '',
    businessName: '',
    businessAddress: '',
    vistarAccountNumber: '',
    vendorSupplyAccountNumber: '',
    stacyWilliamsAccountNumber: '',
    petesCoffeeDirectAccountNumber: '',
    nespressoDirectAccountNumber: '',
    keurigDirectAccountNumber: '',
    contractName: '',
    contractTitle: '',
    action: 'submitRegistration'

  });
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{ success: boolean; message: string } | null>(null);
  const [agreeToTerms, setAgreeToTerms] = useState<boolean | null>(false);
  const [agreeToSignature, setAgreeToSignature] = useState<boolean | null>(false);

  const checkHandler = () => {
     setAgreeToTerms(true)
  }

  const signatureHandler = () => {
      setAgreeToSignature(true)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setSubmitStatus(null); // Clear status on input change
  };

  const handleRecaptchaChange = (token: string | null) => {
    setRecaptchaToken(token);
    setSubmitStatus(null); // Clear status on recaptcha change
  };

  const currentDate: Date = new Date();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitStatus(null);

    if (!recaptchaToken) {
      setSubmitStatus({ success: false, message: 'Please complete the reCAPTCHA verification.' });
      return;
    }

      if (!agreeToTerms) {
          setSubmitStatus({ success: false, message: 'Accept Terms of service' });
          return;
      }

      if (!agreeToSignature) {
          setSubmitStatus({ success: false, message: 'Accept the use of electronic signature' });
          return;
      }

      setIsSubmitting(true);

    try {
      const result = await submitRegistration(formData, recaptchaToken);
      setSubmitStatus(result);
      if (result.success) {
        // Optionally reset form on success
        setFormData({ /* reset state */
            name: '', email: '', phoneNumber: '', businessName: '', businessAddress: '',
            vistarAccountNumber: '', vendorSupplyAccountNumber: '', stacyWilliamsAccountNumber: '',
            petesCoffeeDirectAccountNumber: '', nespressoDirectAccountNumber: '', keurigDirectAccountNumber: '',
            contractName: '', contractTitle: '', action: 'submitRegistration'

        });
        recaptchaRef.current?.reset(); // Reset reCAPTCHA
        setRecaptchaToken(null);
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus({ success: false, message: 'An unexpected error occurred. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormWrapper onSubmit={handleSubmit}>
      <FormTitle>Register for Rebates</FormTitle>

      {/* Basic Info */} 
      <FormGroup>
        <Label htmlFor="name">Name *</Label>
        <Input type="text" id="name" name="name" value={formData.name} onChange={handleInputChange} required />
      </FormGroup>
      <FormGroup>
        <Label htmlFor="email">Email *</Label>
        <Input type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} required />
      </FormGroup>
      <FormGroup>
        <Label htmlFor="phoneNumber">Phone Number *</Label>
        <Input type="tel" id="phoneNumber" name="phoneNumber" value={formData.phoneNumber} onChange={handleInputChange} required />
      </FormGroup>
      <FormGroup>
        <Label htmlFor="businessName">Business Name *</Label>
        <Input type="text" id="businessName" name="businessName" value={formData.businessName} onChange={handleInputChange} required />
      </FormGroup>
      <FormGroup>
        <Label htmlFor="businessAddress">Business Address *</Label>
        <Input type="text" id="businessAddress" name="businessAddress" value={formData.businessAddress} onChange={handleInputChange} required />
      </FormGroup>

      {/* Account Numbers (Optional) */}
      <FormGroup>
        <Label htmlFor="vistarAccountNumber">Vistar Account Number</Label>
        <Input type="text" id="vistarAccountNumber" name="vistarAccountNumber" value={formData.vistarAccountNumber} onChange={handleInputChange} />
      </FormGroup>
      <FormGroup>
        <Label htmlFor="vendorSupplyAccountNumber">Vendor Supply Account Number</Label>
        <Input type="text" id="vendorSupplyAccountNumber" name="vendorSupplyAccountNumber" value={formData.vendorSupplyAccountNumber} onChange={handleInputChange} />
      </FormGroup>
      <FormGroup>
        <Label htmlFor="stacyWilliamsAccountNumber">Stacy Williams Account Number</Label>
        <Input type="text" id="stacyWilliamsAccountNumber" name="stacyWilliamsAccountNumber" value={formData.stacyWilliamsAccountNumber} onChange={handleInputChange} />
      </FormGroup>
       <FormGroup>
        <Label htmlFor="petesCoffeeDirectAccountNumber">Peet's Coffee Direct Account Number</Label>
        <Input type="text" id="petesCoffeeDirectAccountNumber" name="petesCoffeeDirectAccountNumber" value={formData.petesCoffeeDirectAccountNumber} onChange={handleInputChange} />
      </FormGroup>
      {/*  <FormGroup>
        <Label htmlFor="nespressoDirectAccountNumber">Nespresso Direct Account Number</Label>
        <Input type="text" id="nespressoDirectAccountNumber" name="nespressoDirectAccountNumber" value={formData.nespressoDirectAccountNumber} onChange={handleInputChange} />
      </FormGroup>

      <FormGroup>
        <Label htmlFor="keurigDirectAccountNumber">Keurig Direct Account Number</Label>
        <Input type="text" id="keurigDirectAccountNumber" name="keurigDirectAccountNumber" value={formData.keurigDirectAccountNumber} onChange={handleInputChange} />
        <SmallText>Must not be presently receiving Keurig rebates.</SmallText>
      </FormGroup> */}




        <TermsWrapper>
            <FormTitle>Contract Details</FormTitle>

            <TermsWrapperOne >

                <p>
                    Vending Rebates partners with Entegra / Sodexo to earn you agreed upon rebates for the Included Manufacturers listed below. The rebate program is reviewed for growth, volume and membership every twenty-seven months. At that time, it allows us to negotiate a longer term, higher rebates, and more rebatable products. By signing below you agree to the following:
                </p>
                <ul>
                    <li>Remain in the Vending Rebates program for twenty-seven months from the date the agreement is signed</li>
                    <li>Earn rebates for the Included Manufacturers</li>
                    <li>Allow Vending Rebates to obtain the data from the distributor(s)</li>
                    <li>You do not currently get rebates for any of the Included Manufacturers</li>
                </ul>
                <p>
                    Vending Rebates will reach out to members to negotiate further terms and conditions within the last three months of the agreement. You will not be able to end the agreement early but there will be no auto-renewal for the agreement. The reason we do not allow members to end the agreement early is because it hurts our ability to negotiate future rates.
                </p>
                <p>
                    This Agreement will be governed by and construed in accordance with the substantive laws of the State of North Carolina without regard to conflict of laws and all disputes arising under or relating to this Agreement shall be brought and resolved solely and exclusively in the State Court located in Mecklenburg County, NC. Should any legal action be commenced in connection with this Agreement, the prevailing party in such action shall be entitled to recover, in addition to court costs, such amount as the court may adjudge as reasonable attorneys' fees.
                </p>

            </TermsWrapperOne>


            <FormGroup>
                <Label htmlFor="contractName">Name *</Label>
                <Input type="text" id="contractName" name="contractName" value={formData.contractName} onChange={handleInputChange} required />
            </FormGroup>
            <FormGroup>
                <Label htmlFor="contractTitle">Title *</Label>
                <Input type="text" id="contractTitle" name="contractTitle" value={formData.contractTitle} onChange={handleInputChange} required />
            </FormGroup>

            <ContractDate>Date: {currentDate.getMonth()+1}/{currentDate.getDate()}/{currentDate.getFullYear()}</ContractDate>

        </TermsWrapper>

      {/* TODO: Add Multi-select options here if needed */}
        <CheckDiv>
            <input type="checkbox" id="agreeToElecttronicSignature" name=""  onChange={signatureHandler} />
            <label htmlFor="agreeToElecttronicSignature">I agree to the use of an electronic signature</label>
        </CheckDiv>
        <CheckDiv>
            <input type="checkbox" id="agreeToTerms" name=""  onChange={checkHandler} />
            <label htmlFor="agreeToTerms">I agree to Terms of use</label>
        </CheckDiv>

      {/* reCAPTCHA */} 
      <RecaptchaWrapper>
        <ReCAPTCHA
          ref={recaptchaRef}
          sitekey={RECAPTCHA_SITE_KEY} 
          onChange={handleRecaptchaChange}
        />
      </RecaptchaWrapper>

      {/* Submission Status */} 
      {submitStatus && (
        <StatusMessage success={submitStatus.success}>
          {submitStatus.message}
        </StatusMessage>
      )}

      {/* Submit Button */}
      <SubmitButton type="submit" disabled={isSubmitting || !recaptchaToken || !agreeToTerms || !signatureHandler}>
        {isSubmitting ? 'Submitting...' : 'Register'}
      </SubmitButton>
    </FormWrapper>
  );
};

export default RegistrationForm; 